import React, { useEffect, useMemo, useState } from 'react';

import type { UUID } from '@aline/common';
import { getIsWorkflowPhased } from '@aline/common';
import { useApolloClient } from '@apollo/client/react';
import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Skeleton } from '@mui/material';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import {
  ChevronDown,
  CircleCheck,
  GripVertical,
  ListTodo,
  Loader,
  Signature,
  Trash2,
} from 'lucide-react';
import {
  getAreAllTasksDone,
  getTaskProgress,
} from 'src/components/workflow/board/workflow-card/task-progress';
import { Apollo } from 'src/gql/generated';
import type { Apollo as ApolloTypes } from 'src/gql/generated';
import { getIsSigningPhaseType, getWorkflowWasSentForSig } from 'src/workflow/helpers';
import { makeStyles } from 'tss-react/mui';

import { BetweenPhasesConnector } from './BetweenPhasesConnector';
import { phasesDropAnimation, usePhasesDnd } from './hooks/use-phases-dnd';
import { PhaseCard, PhaseCardSkeleton } from './PhaseCard';
import { PhaselessTaskList } from './PhaselessTaskList';
import { PhaseAddMenu, PhasesHeader } from './PhasesHeader';
import type { PhaseAddOptions } from './PhasesHeader';
import { SigningPhaseCard } from './SigningPhaseCard';
import { UnassignedRolesAlert } from './UnassignedRolesAlert';
import { useAddPhase } from './use-add-phase';
import { IntakeFormBuilderModal } from '../intake-form/IntakeFormBuilderModal';
import { IntakeFormInstanceCard } from '../intake-form/IntakeFormInstanceCard';
import { IntakeFormPhaseCard } from '../intake-form/IntakeFormPhaseCard';
import { LinkIntakeFormDialog } from '../intake-form/LinkIntakeFormDialog';
import type { ParkedWorkflowMeta } from '../use-parked-workflow-completion';
import { WorkflowStatusBanner } from '../WorkflowStatusBanner';

type WorkflowPhasesQuery = ApolloTypes.GetWorkflowPhasesQuery;
type LoadedWorkflow = NonNullable<WorkflowPhasesQuery['workflow']>;
type WorkflowPhase = LoadedWorkflow['phases'][number];
type WorkflowPhasesChangedEvent =
  ApolloTypes.OnWorkflowPhasesChangedSubscription['workflowPhasesChanged'];
type WorkflowPhaseRowsChangedEvent = Exclude<
  WorkflowPhasesChangedEvent,
  | { __typename: 'WorkflowIntakeFormAddedEvent' }
  | { __typename: 'WorkflowIntakeFormRemovedEvent' }
  | { __typename: 'WorkflowIntakeFormUpdatedEvent' }
>;

export function sortWorkflowPhasesByOrder<TPhase extends { id: string; order: number }>(
  phases: TPhase[]
): TPhase[] {
  return [...phases].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

export function upsertWorkflowPhases(
  existing: WorkflowPhase[],
  incoming: WorkflowPhase[]
): WorkflowPhase[] {
  const incomingById = new Map(incoming.map(phase => [phase.id, phase]));
  const existingIds = new Set(existing.map(phase => phase.id));

  return sortWorkflowPhasesByOrder([
    ...existing.map(phase => incomingById.get(phase.id) ?? phase),
    ...incoming.filter(phase => !existingIds.has(phase.id)),
  ]);
}

export function removeWorkflowPhase(existing: WorkflowPhase[], phaseId: UUID): WorkflowPhase[] {
  return existing.filter(phase => phase.id !== phaseId);
}

export function applyWorkflowPhasesChangedEvent(
  previous: WorkflowPhasesQuery,
  event: WorkflowPhasesChangedEvent
): WorkflowPhasesQuery {
  const workflow = previous.workflow;
  if (!workflow || workflow.id !== event.workflowId) return previous;

  switch (event.__typename) {
    case 'WorkflowIntakeFormAddedEvent':
    case 'WorkflowIntakeFormUpdatedEvent':
      return {
        ...previous,
        workflow: {
          ...workflow,
          intakeFormTemplate: event.intakeFormTemplate,
        },
      };
    case 'WorkflowIntakeFormRemovedEvent':
      return {
        ...previous,
        workflow: {
          ...workflow,
          intakeFormTemplate: null,
        },
      };
    default: {
      const phases = applyWorkflowPhasesEventToRows(workflow.phases, event);
      return { ...previous, workflow: { ...workflow, phases } };
    }
  }
}

const useStyles = makeStyles()(theme => ({
  activeChipStatus: {
    alignItems: 'center',
    border: `1px solid ${theme.appColors.primary ?? theme.palette.primary.main}`,
    borderRadius: 12,
    color: theme.appColors.primary ?? theme.palette.primary.main,
    display: 'flex',
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 'bold',
    gap: 2,
    padding: theme.spacing(0.25, 1),
  },
  addRow: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  // Stacked banners sit close together; the stack disappears when neither shows.
  banners: {
    '&:empty': { display: 'none' },
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  boldCount: {
    fontWeight: 'bold',
  },
  completedChipStatus: {
    alignItems: 'center',
    color: '#00866a',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 'bold',
    gap: 2,
    whiteSpace: 'nowrap',
  },
  dragHandle: {
    '&:active': { cursor: 'grabbing' },
    alignItems: 'center',
    alignSelf: 'center',
    color: theme.appColors.iconsType ?? 'rgba(55, 73, 81, 0.5)',
    cursor: 'grab',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    touchAction: 'none',
  },
  handleSlot: {
    alignItems: 'center',
    alignSelf: 'stretch',
    boxSizing: 'border-box',
    display: 'flex',
    flexShrink: 0,
    height: '100%',
    justifyContent: 'center',
    left: -24,
    position: 'absolute',
    width: theme.spacing(3),
  },
  headerIcon: {
    color: theme.appColors.iconsType,
    fontSize: 24,
  },
  overlayCard: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: '2px dashed #BDBDBD',
    borderRadius: 12,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    height: 56,
    justifyContent: 'space-between',
    padding: theme.spacing(0, 2),
    width: '100%',
  },
  overlayCardActions: {
    alignItems: 'center',
    color: theme.appColors.iconsType ?? 'rgba(55, 73, 81, 0.5)',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(0.5),
  },
  overlayCardActive: {
    borderColor: theme.appColors.primary ?? theme.palette.primary.main,
  },
  overlayCardContent: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    gap: theme.spacing(1),
    minWidth: 0,
  },
  overlayCardName: {
    fontSize: 16,
    fontWeight: 'bold',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  phaseRow: {
    alignItems: 'stretch',
    boxSizing: 'border-box',
    display: 'flex',
    position: 'relative',
    width: '100%',
  },
  phaseRowContent: {
    boxSizing: 'border-box',
    display: 'flex',
    flex: 1,
    minWidth: 0,
  },
  phasesInset: {
    boxSizing: 'border-box',
    width: '100%',
  },
  phasesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
  },
  previousDivider: {
    backgroundColor: theme.appColors.rule,
    flex: 1,
    height: 1,
  },
  previousLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    padding: theme.spacing(0, 2),
    whiteSpace: 'nowrap',
  },
  previousRoot: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    width: '100%',
  },
  previousRow: {
    alignItems: 'center',
    display: 'flex',
    width: '100%',
  },
  previousToggle: {
    background: 'none',
    border: 'none',
    color: theme.appColors.primary ?? theme.palette.primary.main,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    padding: 0,
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
  },
  statusText: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 600,
  },
}));

interface PhasesSectionProps {
  workflowId: UUID;
  aiIsGenerating?: boolean;
}

export function PhasesSection({ aiIsGenerating = false, workflowId }: PhasesSectionProps) {
  const client = useApolloClient();
  const { classes } = useStyles();
  const [showPrevious, setShowPrevious] = useState(false);
  const [newPhaseId, setNewPhaseId] = useState<string | null>(null);
  const [intakeBuilderOpen, setIntakeBuilderOpen] = useState(false);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const { data: phasesData } = Apollo.useGetWorkflowPhasesQuery({
    variables: { workflowId },
  });

  Apollo.useOnWorkflowPhasesChangedSubscription({
    onData: ({ data: subscriptionData }) => {
      const event = subscriptionData.data?.workflowPhasesChanged;
      if (!event) return;

      client.cache.updateQuery<
        ApolloTypes.GetWorkflowPhasesQuery,
        ApolloTypes.GetWorkflowPhasesQueryVariables
      >(
        {
          query: Apollo.GetWorkflowPhasesDocument,
          variables: { workflowId },
        },
        previous => {
          if (!previous?.workflow) return previous;

          return applyWorkflowPhasesChangedEvent(previous, event);
        }
      );
    },
    onError: error => {
      Sentry.captureException(error, {
        extra: { subscription: 'WorkflowPhasesChanged', workflowId },
      });
    },
    variables: { workflowId },
  });

  const phases = useMemo(() => phasesData?.workflow?.phases ?? [], [phasesData]);
  const workflow = phasesData?.workflow ?? null;
  const isTemplateWorkflow = workflow?.variant === Apollo.WorkflowVariant.Template;
  const intakeFormTemplate = (isTemplateWorkflow && workflow?.intakeFormTemplate) || null;
  // Intake forms attach only to template workflows, one per template; the add
  // menu offers "Add"/"Link" only until one exists.
  const canAddIntake = isTemplateWorkflow && !intakeFormTemplate;
  const { activeDraggingPhase, dndHandlers, sensors } = usePhasesDnd(
    phases,
    workflowId,
    aiIsGenerating
  );

  // Builder (create-new) + link picker are triggered from the add menu, so they mount at the root.
  const intakeModalsNode = isTemplateWorkflow && !aiIsGenerating && (
    <>
      <IntakeFormBuilderModal
        existingTemplate={null}
        open={intakeBuilderOpen}
        workflowId={workflowId}
        onClose={() => setIntakeBuilderOpen(false)}
      />
      <LinkIntakeFormDialog
        open={linkPickerOpen}
        workflowId={workflowId}
        onClose={() => setLinkPickerOpen(false)}
      />
    </>
  );
  const signingPhase = phases.find(getIsSigningPhase);
  const signingPhaseOrder = signingPhase?.order ?? null;
  const allPhasesComplete = phases.length > 0 && phases.every(getIsPhaseComplete);
  const firstIncompleteIndex = phases.findIndex(p => !getIsPhaseComplete(p));
  const completedLeadingCount = firstIncompleteIndex === -1 ? phases.length : firstIncompleteIndex;
  const hasCompletedLeading = completedLeadingCount > 0 && !allPhasesComplete;
  const activePhase = phases.find(p => Boolean(p.startedAt) && !getIsPhaseComplete(p));

  useEffect(() => {
    if (!newPhaseId) return;
    if (!phases.some(p => p.id === newPhaseId)) return;
    document
      .getElementById(`phase-${newPhaseId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    setNewPhaseId(null);
  }, [newPhaseId, phases]);

  const { addPhase } = useAddPhase(workflowId);
  const handleInsertPhase = async (order: number) => {
    const id = await addPhase({ isSigning: false, order });
    if (id) setNewPhaseId(id);
  };

  /**
   * Skeleton only while there's nothing to show. Gating on `loading` would
   * blank the section (and reset the page scroll) on every push-invalidation
   * refetch — Apollo 4's notifyOnNetworkStatusChange defaults to true, so
   * refetch flips `loading` even though the cached phases are still valid.
   */
  if (!workflow) return <PhasesSectionSkeleton />;

  const { boardId } = workflow;
  const workflowColumnCategory = workflow.column.category;
  const intakeCard = (
    <IntakeCard
      intakeForm={workflow.intakeForm}
      intakeFormTemplate={intakeFormTemplate}
      readOnly={aiIsGenerating}
      workflowId={workflowId}
    />
  );
  const addOptions: PhaseAddOptions = {
    canAddIntake,
    hasSigningPhase: Boolean(signingPhase),
    onAddIntake: () => setIntakeBuilderOpen(true),
    onLinkIntake: () => setLinkPickerOpen(true),
    onPhaseCreated: setNewPhaseId,
    phaseCount: phases.length,
    workflowId,
  };
  const onlyPhase = phases.at(0);

  if (
    !getIsWorkflowPhased(phases, getIsSigningPhaseType) &&
    (!onlyPhase || getIsNonSigningPhase(onlyPhase))
  )
    return (
      <div className={classes.root}>
        <div className={classes.banners}>
          <WorkflowStatusBanner phases={phases} workflow={workflow} />
          <UnassignedRolesAlert workflowId={workflowId} />
        </div>
        <PhaselessTaskList
          addOptions={addOptions}
          phase={onlyPhase ?? null}
          readOnly={aiIsGenerating}
          workflow={workflow}
        >
          {intakeCard}
        </PhaselessTaskList>
        {aiIsGenerating && (
          <PhaseCardSkeleton aiIsGenerating label="AI generating tasks..." taskCount={3} />
        )}
        {intakeModalsNode}
      </div>
    );

  return (
    <div className={classes.root}>
      <div className={classes.banners}>
        <WorkflowStatusBanner phases={phases} workflow={workflow} />
        <UnassignedRolesAlert workflowId={workflowId} />
      </div>
      <PhasesHeader addOptions={addOptions} aiIsGenerating={aiIsGenerating} />
      <div className={classes.phasesList}>
        {intakeCard}
        <DndContext collisionDetection={closestCenter} sensors={sensors} {...dndHandlers}>
          <SortableContext items={phases.map(p => p.id)} strategy={verticalListSortingStrategy}>
            {hasCompletedLeading &&
              showPrevious &&
              phases
                .slice(0, completedLeadingCount)
                .map(phase => (
                  <SortablePhaseWrapper
                    key={phase.id}
                    boardId={boardId}
                    isActive={false}
                    phase={phase}
                    readOnly={aiIsGenerating}
                    signingPhaseOrder={signingPhaseOrder}
                    workflow={workflow}
                    workflowColumnCategory={workflowColumnCategory}
                  />
                ))}
            {hasCompletedLeading && (
              <PreviousPhasesBanner
                count={completedLeadingCount}
                expanded={showPrevious}
                onToggle={() => setShowPrevious(prev => !prev)}
              />
            )}
            {/**
             * The connector is the gutter between two cards, so the first card of this
             * run never gets one — above it sits the previously-completed banner (or the
             * section header), not a phase to insert after.
             */}
            {phases
              .slice(hasCompletedLeading ? completedLeadingCount : 0)
              .map((phase, index, visiblePhases) => (
                <React.Fragment key={phase.id}>
                  {!aiIsGenerating && index > 0 && (
                    <BetweenPhasesConnector
                      insertOrder={phase.order}
                      isAfterActivePhase={visiblePhases.at(index - 1)?.id === activePhase?.id}
                      onAddPhase={order => void handleInsertPhase(order)}
                    />
                  )}
                  <SortablePhaseWrapper
                    boardId={boardId}
                    isActive={phase.id === activePhase?.id}
                    phase={phase}
                    readOnly={aiIsGenerating}
                    signingPhaseOrder={signingPhaseOrder}
                    workflow={workflow}
                    workflowColumnCategory={workflowColumnCategory}
                  />
                </React.Fragment>
              ))}
          </SortableContext>
          <DragOverlay dropAnimation={phasesDropAnimation}>
            {activeDraggingPhase && (
              <PhaseDragOverlayCard
                phase={activeDraggingPhase}
                signingPhaseOrder={signingPhaseOrder}
              />
            )}
          </DragOverlay>
        </DndContext>
        {aiIsGenerating && (
          <PhaseCardSkeleton aiIsGenerating label="AI generating phase..." taskCount={3} />
        )}
        {!aiIsGenerating && (
          <div className={classes.addRow}>
            <PhaseAddMenu {...addOptions} />
          </div>
        )}
      </div>
      {intakeModalsNode}
    </div>
  );
}

interface IntakeCardProps {
  intakeForm: LoadedWorkflow['intakeForm'];
  intakeFormTemplate: LoadedWorkflow['intakeFormTemplate'];
  readOnly: boolean;
  workflowId: UUID;
}

/**
 * The intake form renders as a fixed virtual phase-0 card, but only once one is
 * attached: a config card on template workflows, a fillable instance card on
 * launched ones.
 */
function IntakeCard({ intakeForm, intakeFormTemplate, readOnly, workflowId }: IntakeCardProps) {
  const { classes } = useStyles();
  if (intakeFormTemplate)
    return (
      <div className={classes.phasesInset}>
        <IntakeFormPhaseCard
          intakeFormTemplate={intakeFormTemplate}
          readOnly={readOnly}
          workflowId={workflowId}
        />
      </div>
    );
  if (intakeForm)
    return (
      <div className={classes.phasesInset}>
        <IntakeFormInstanceCard intakeForm={intakeForm} workflowId={workflowId} />
      </div>
    );
  return null;
}

interface PreviousPhasesBannerProps {
  count: number;
  expanded: boolean;
  onToggle: () => void;
}

function PreviousPhasesBanner({ count, expanded, onToggle }: PreviousPhasesBannerProps) {
  const { classes } = useStyles();

  return (
    <div className={classes.previousRoot}>
      <div className={classes.previousRow}>
        <div className={classes.previousDivider} />
        <span className={classes.previousLabel}>
          {`${count} Previously Completed Phase${count > 1 ? 's' : ''}`}
        </span>
        <div className={classes.previousDivider} />
      </div>
      <button className={classes.previousToggle} type="button" onClick={onToggle}>
        {expanded ? 'Hide' : 'Show all'}
      </button>
    </div>
  );
}

function PhaseIcon({ isSigning }: { isSigning: boolean }) {
  if (isSigning) return <Signature size={16} strokeWidth={2} />;
  return <ListTodo size={16} strokeWidth={2} />;
}

function PhaseStatusBadge({ phase }: { phase: ApolloTypes.PhaseSimpleFragment }) {
  const { classes } = useStyles();
  if (getIsNonSigningPhase(phase) && getIsPhaseComplete(phase)) {
    const taskCount = getTaskProgress(phase.tasks).total;
    return (
      <div className={classes.completedChipStatus}>
        <CircleCheck color="#00866a" size={16} />
        <span>
          {taskCount} {taskCount === 1 ? 'task' : 'tasks'}
        </span>
      </div>
    );
  }

  if (phase.completedAt)
    return (
      <div className={classes.completedChipStatus}>
        <CircleCheck color="#00866a" size={16} />
      </div>
    );

  if (phase.startedAt)
    return (
      <div className={classes.activeChipStatus}>
        <Loader size={14} />
        <span>Active</span>
      </div>
    );

  return null;
}

function PhaseCardActions({
  phase,
  signingPhaseOrder,
}: {
  phase: ApolloTypes.PhaseSimpleFragment;
  signingPhaseOrder: number | null;
}) {
  const { classes } = useStyles();

  return (
    <div className={classes.overlayCardActions}>
      <PhaseStatusBadge phase={phase} />
      {getCanModifyPhaseStructure(phase, signingPhaseOrder) && <Trash2 size={18} />}
      <ChevronDown size={18} />
    </div>
  );
}

function PhaseCardForType({
  boardId,
  isActive,
  phase,
  readOnly,
  signingPhaseOrder,
  workflow,
  workflowColumnCategory,
}: {
  boardId: string;
  isActive: boolean;
  phase: ApolloTypes.PhaseSimpleFragment;
  readOnly: boolean;
  signingPhaseOrder: number | null;
  workflow: ParkedWorkflowMeta;
  workflowColumnCategory: Apollo.ColumnCategory;
}) {
  if (getIsSigningPhase(phase))
    return (
      <SigningPhaseCard
        boardId={boardId}
        isActive={isActive}
        phase={phase}
        readOnly={readOnly}
        workflowColumnCategory={workflowColumnCategory}
        workflowId={workflow.id}
      />
    );

  return (
    <PhaseCard
      boardId={boardId}
      canModifyStructure={getCanModifyPhaseStructure(phase, signingPhaseOrder)}
      isActive={isActive}
      phase={phase}
      readOnly={readOnly}
      workflow={workflow}
      workflowColumnCategory={workflowColumnCategory}
    />
  );
}

interface SortablePhaseWrapperProps {
  boardId: UUID;
  isActive: boolean;
  phase: ApolloTypes.PhaseSimpleFragment;
  readOnly: boolean;
  signingPhaseOrder: number | null;
  workflow: ParkedWorkflowMeta;
  workflowColumnCategory: Apollo.ColumnCategory;
}

function SortablePhaseWrapper({
  boardId,
  isActive,
  phase,
  readOnly,
  signingPhaseOrder,
  workflow,
  workflowColumnCategory,
}: SortablePhaseWrapperProps) {
  const { classes } = useStyles();
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    disabled: readOnly,
    id: phase.id,
  });

  const style: React.CSSProperties = {
    backgroundColor: isDragging ? '#F5F5F5' : undefined,
    border: isDragging ? '2px dashed #BDBDBD' : undefined,
    borderRadius: isDragging ? 12 : undefined,
    opacity: isDragging ? 0.4 : 1,
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} className={classes.phaseRow} id={`phase-${phase.id}`} style={style}>
      {!readOnly && (
        <div className={classes.handleSlot}>
          <div className={classes.dragHandle} {...listeners} {...attributes}>
            <GripVertical size={18} />
          </div>
        </div>
      )}
      <div className={classes.phaseRowContent}>
        <PhaseCardForType
          boardId={boardId}
          isActive={isActive}
          phase={phase}
          readOnly={readOnly}
          signingPhaseOrder={signingPhaseOrder}
          workflow={workflow}
          workflowColumnCategory={workflowColumnCategory}
        />
      </div>
    </div>
  );
}

// Whether the workflow is phased isn't known until the phases load, so the placeholder is neutral.
function PhasesSectionSkeleton() {
  return <Skeleton height={320} sx={{ borderRadius: 2 }} variant="rounded" width="100%" />;
}

function PhaseDragOverlayCard({
  phase,
  signingPhaseOrder,
}: {
  phase: ApolloTypes.PhaseSimpleFragment;
  signingPhaseOrder: number | null;
}) {
  const { classes } = useStyles();
  const isActive = Boolean(phase.startedAt) && !getIsPhaseComplete(phase);

  return (
    <div className={classes.phaseRow} style={{ cursor: 'grabbing', pointerEvents: 'none' }}>
      <div className={classes.handleSlot}>
        <div className={classes.dragHandle}>
          <GripVertical size={18} />
        </div>
      </div>
      <div className={classes.phaseRowContent}>
        <div className={clsx(classes.overlayCard, isActive && classes.overlayCardActive)}>
          <div className={classes.overlayCardContent}>
            <PhaseIcon isSigning={phase.__typename === 'SigningPhase'} />
            <span className={classes.overlayCardName}>{phase.name || 'Untitled phase'}</span>
          </div>
          <PhaseCardActions phase={phase} signingPhaseOrder={signingPhaseOrder} />
        </div>
      </div>
    </div>
  );
}

/**
 * Once a workflow is sent for signature, the signing phase and everything ordered before it are
 * frozen. Post-signing phases remain mutable while their work is outstanding.
 */
function getCanModifyPhaseStructure(
  phase: ApolloTypes.PhaseSimpleFragment,
  signingPhaseOrder: number | null
) {
  const wasSentForSig = getWorkflowWasSentForSig(phase.workflow);
  if (getIsSigningPhase(phase)) return !wasSentForSig;
  if (wasSentForSig && signingPhaseOrder !== null && phase.order < signingPhaseOrder) return false;
  return !getAreAllTasksDone(phase.tasks);
}

// A task phase counts as complete once its tasks and their sub-tasks are all done.
function getIsPhaseComplete(phase: ApolloTypes.PhaseSimpleFragment) {
  if (getIsNonSigningPhase(phase)) return getAreAllTasksDone(phase.tasks);
  return Boolean(phase.completedAt);
}

function getIsNonSigningPhase(
  phase: ApolloTypes.PhaseSimpleFragment
): phase is ApolloTypes.PhaseSimple_NonSigningPhase_Fragment {
  return phase.__typename === 'NonSigningPhase';
}

function getIsSigningPhase(
  phase: ApolloTypes.PhaseSimpleFragment
): phase is ApolloTypes.PhaseSimple_SigningPhase_Fragment {
  return phase.__typename === 'SigningPhase';
}

function applyWorkflowPhasesEventToRows(
  phases: WorkflowPhase[],
  event: WorkflowPhaseRowsChangedEvent
): WorkflowPhase[] {
  switch (event.__typename) {
    case 'WorkflowPhaseCreatedEvent':
      return upsertWorkflowPhases(phases, [event.phase]);
    case 'WorkflowPhaseDeletedEvent':
      return removeWorkflowPhase(phases, event.phaseId);
    case 'WorkflowPhaseUpdatedEvent':
    case 'WorkflowPhasesReorderedEvent':
      return upsertWorkflowPhases(phases, event.phases);
  }
}
